import { CheckCircle2, GripVertical, X } from "lucide-react";

import { cn } from "@/lib/utils";

export interface ProcessSelectionRow {
  id: number;
  partProcessId: number;
  processId: number;
  processNo: string;
  processName: string;
  shopName: string;
  processStandardHour?: number;
  labourStandardHour?: number;
  rwwsIndex?: string;
  workchestraProcessName?: string;
}

interface SelectedProcessOverviewProps {
  selectedProcesses: ProcessSelectionRow[];
  className?: string;
  onRemoveProcess?: (partProcessId: number) => void;
  mode?: "editable" | "timeline";
  helperText?: string;
}

export const parseProcessName = (fullProcessName?: string | null) => {
  if (!fullProcessName) {
    return {
      processNo: "-",
      processName: "-"
    };
  }

  const [processNo, ...processNameParts] = fullProcessName.split(" - ");

  return {
    processNo,
    processName:
      processNameParts.length > 0
        ? processNameParts.join(" - ")
        : fullProcessName
  };
};

export const mapPartProcessesToRows = (
  items: any[] = []
): ProcessSelectionRow[] =>
  items
    .map((item: any) => {
      const partProcessId = Number(item.id);
      const processId = Number(item.processId ?? item?.ppsProcess?.id);
      const processMeta = parseProcessName(item?.ppsProcess?.processName);

      return {
        id: partProcessId,
        partProcessId,
        processId,
        processNo: processMeta.processNo,
        processName: processMeta.processName,
        shopName: item?.ppsShop?.shopName ?? "-",
        processStandardHour: item?.processStandardHours
      };
    })
    .filter(
      (item: ProcessSelectionRow) =>
        item.partProcessId > 0 && Number.isFinite(item.processId)
    );

export const SelectedProcessOverview = ({
  selectedProcesses,
  className,
  onRemoveProcess,
  mode = "editable",
  helperText
}: SelectedProcessOverviewProps) => {
  const formatStandardHour = (processStandardHour?: number) => {
    if (typeof processStandardHour !== "number") {
      return "-";
    }

    return `${processStandardHour.toFixed(2)}h`;
  };

  return (
    <div
      className={cn(
        "aero:rounded-md aero:border aero:px-4 aero:py-3",
        mode === "editable"
          ? "aero:border-[#BFDBFE] aero:bg-[#EEF4FF]"
          : "aero:border-neutral-300 aero:bg-white",
        className
      )}
    >
      <div className="aero:flex aero:flex-col aero:gap-1">
        <div className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Selected Process Steps ({selectedProcesses.length})
        </div>
        {mode === "editable" && selectedProcesses.length > 0 && (
          <div className="aero:text-xs aero:text-neutral-500">
            {helperText ||
              "Steps are listed based on your current selections, and table step labels are updated automatically."}
          </div>
        )}
      </div>
      {selectedProcesses.length === 0 ? (
        <div className="aero:mt-3 aero:text-sm aero:text-neutral-900">
          No selected data
        </div>
      ) : (
        <div
          className={cn(
            "aero:mt-4",
            mode === "timeline" ? "aero:space-y-2.5" : "aero:space-y-2"
          )}
        >
          {selectedProcesses.map((process, index) => (
            <div
              key={process.partProcessId}
              className={cn(
                "aero:flex aero:items-start aero:justify-between aero:gap-2 aero:text-sm aero:text-neutral-900",
                mode === "timeline"
                  ? "aero:rounded-md aero:border aero:border-neutral-300 aero:bg-white aero:px-3 aero:py-2.5"
                  : "aero:rounded-sm aero:border aero:border-neutral-300 aero:bg-white aero:px-2.5 aero:py-1.5"
              )}
            >
              <div className="aero:flex aero:min-w-0 aero:flex-1 aero:items-start aero:gap-2.5">
                {mode === "timeline" ? (
                  <div className="aero:flex aero:h-[18px] aero:w-[18px] aero:shrink-0 aero:items-center aero:justify-center aero:rounded-full aero:bg-[#2F5F5A] aero:text-[11px] aero:font-semibold aero:text-white">
                    {index + 1}
                  </div>
                ) : (
                  <GripVertical className="aero:mt-0.5 aero:h-4 aero:w-4 aero:shrink-0 aero:text-neutral-400" />
                )}

                <div className="aero:min-w-0 aero:flex-1">
                  {mode === "timeline" ? (
                    <>
                      <div className="aero:truncate aero:text-sm aero:text-neutral-900">
                        <span className="aero:font-bold">
                          {process.processNo} - {process.processName}
                        </span>
                        {process.shopName && process.shopName !== "-" && (
                          <span className="aero:text-neutral-700">
                            {` (${process.shopName})`}
                          </span>
                        )}
                      </div>
                      <div className="aero:mt-1 aero:flex aero:items-center aero:gap-2 aero:text-xs aero:text-neutral-600">
                        <CheckCircle2 className="aero:h-4 aero:w-4 aero:text-neutral-500" />
                        <span>Not Started</span>
                        <span className="aero:h-1 aero:w-1 aero:rounded-full aero:bg-neutral-400" />
                        <span className="aero:text-[#2563EB]">
                          Standard Hour (
                          {formatStandardHour(process.processStandardHour)})
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="aero:truncate aero:text-sm aero:text-neutral-900">
                      <span className="aero:font-bold">
                        {process.processNo} - {process.processName}
                      </span>
                      {process.shopName && process.shopName !== "-" && (
                        <span className="aero:text-neutral-700">
                          {` (${process.shopName})`}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {onRemoveProcess && (
                <button
                  type="button"
                  onClick={() => onRemoveProcess(process.partProcessId)}
                  className="aero:flex aero:h-6 aero:w-6 aero:shrink-0 aero:items-center aero:justify-center aero:rounded aero:text-red-500 hover:aero:bg-red-50"
                  aria-label={`Remove ${process.processName}`}
                >
                  <X className="aero:h-4 aero:w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
